import React from 'react'
import { useTheme } from '@mui/material'
import MedidorDeSaldo from './MedidorDeSaldo'
import { tokensDoDisparo } from './tokens'
import { reais, numero } from '../../../libs/disparos'
import { TEMPLATE_CATEGORY_LABELS } from '../../../libs/metaTemplates'
import { linkDeRecargaSeguro } from '../../../libs/suspensaoOficial'
import './disparo.css'

// Recibo do disparo: para quem vai, o quê, quanto custa no máximo, o medidor
// de saldo, o veredito do master e a ÚNICA ação principal. O veredito vem da
// conferência do servidor; aqui é só exibição (a criação confere de novo).
//
// `informativo` (agendamento): custo de UMA ocorrência, sem botão e sem as
// escolhas de recarga — o agendamento não é barrado aqui, cada mensagem é
// conferida na hora do envio.

const POR_QUE_ATE =
  'É um teto: só é cobrada a mensagem que a Meta aceitar. O valor pode mudar se o mês virar durante o envio, ' +
  'se a Meta reclassificar o modelo antes de sair, se o atendimento do GLA Conecta usar o mesmo saldo ' +
  'ou se outra conexão sua mudar a sua faixa da régua no mesmo dia.'

// Agendamento: a conferência olha o expediente de AGORA, que não diz nada
// sobre um envio marcado para outra hora — esse motivo some do recibo.
function paraOAgendamento(conferencia) {
  if (!conferencia) return conferencia
  const motivos = (conferencia.motivos || []).filter((m) => !String(m.codigo || '').startsWith('fora_do_expediente'))
  if (conferencia.veredito !== 'impedido' || motivos.some((m) => m.tipo === 'impedimento')) {
    return { ...conferencia, motivos }
  }
  const veredito = motivos.some((m) => m.tipo === 'saldo') ? 'saldo_insuficiente' : 'liberado'
  return { ...conferencia, motivos, veredito }
}

function Custo({ conferencia, carregando, informativo }) {
  const c = conferencia
  if (!c || !c.custo) {
    return (
      <div className="dp-custo">
        <small>Custo estimado</small>
        <strong className="apagado">—</strong>
        <span className="dp-obs">
          {carregando ? 'Conferindo custo e saldo…' : 'Aparece quando a lista e a mensagem estiverem prontas.'}
        </span>
      </div>
    )
  }

  const { custo } = c
  const cartao = custo.custoMetaNoSeuCartao != null && (
    <span className="dp-obs">Mais o custo Meta no seu cartão: até {reais(custo.custoMetaNoSeuCartao)}.</span>
  )

  if (c.canal === 'qr') {
    return (
      <div className="dp-custo">
        <small>Custo por mensagem</small>
        <strong>Sem custo</strong>
        <span className="dp-obs">
          Na conexão por QR Code o envio segue o ritmo do seu plano e o limite diário de segurança do número.
        </span>
      </div>
    )
  }

  if (!custo.cobravel) {
    return (
      <div className="dp-custo">
        <small>Custo no seu saldo</small>
        <strong>Sem custo</strong>
        {cartao}
      </div>
    )
  }

  const validos = Number(c.destinatarios?.validos) || 0
  const categoria = c.conteudo?.modelo?.categoriaCobrada
  const rotulo = TEMPLATE_CATEGORY_LABELS[categoria]
  return (
    <div className="dp-custo">
      <small>{informativo ? 'Até, a cada ocorrência' : 'Custo de até'}</small>
      <strong>{reais(custo.total)}</strong>
      {validos > 0 && (
        <span className="dp-obs">
          {numero(validos)} {validos === 1 ? 'mensagem' : 'mensagens'}
          {rotulo ? ` de ${rotulo.toLowerCase()}` : ''}, cerca de {reais(custo.total / validos)} cada na sua faixa da régua.
        </span>
      )}
      {cartao}
      <details className="dp-porque">
        <summary>Por que “até”?</summary>
        <p>{POR_QUE_ATE}</p>
      </details>
    </div>
  )
}

function Selo({ conferencia, erro, informativo }) {
  if (erro) {
    return (
      <div className="dp-selo neutro">
        <span className="ic">?</span>
        <div><b>Não foi possível conferir agora</b><span>{erro}</span></div>
      </div>
    )
  }
  const c = conferencia
  if (!c) return null

  if (c.veredito === 'impedido') {
    const bloqueios = (c.motivos || []).filter((m) => m.tipo !== 'aviso')
    return (
      <div className="dp-selo bad" role="alert">
        <span className="ic">!</span>
        <div>
          <b>{informativo ? 'Pontos a conferir' : 'Falta resolver antes de disparar'}</b>
          <ul>{bloqueios.map((m) => <li key={`${m.codigo}-${m.texto}`}>{m.texto}</li>)}</ul>
        </div>
      </div>
    )
  }

  if (c.veredito === 'saldo_insuficiente') {
    const validos = Number(c.destinatarios?.validos) || 0
    const cabem = Number(c.saldo?.cabem) || 0
    return (
      <div className="dp-selo bad" role="alert">
        <span className="ic">!</span>
        <div>
          <b>{informativo ? 'O saldo de agora não cobre uma ocorrência' : 'O saldo não cobre este disparo'}</b>
          <span>
            {informativo
              ? 'Recarregue antes da data. Sem saldo, as mensagens ficam retidas na hora do envio.'
              : `Cabem ${numero(cabem)} das ${numero(validos)} mensagens antes do limite.`}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="dp-selo ok">
      <span className="ic">✓</span>
      <div>
        <b>{informativo ? 'O saldo de agora cobre uma ocorrência' : 'Pronto para disparar'}</b>
        <span>
          {informativo
            ? 'Cada mensagem é conferida de novo na hora do envio.'
            : 'Começa em até 1 minuto e continua mesmo com o navegador fechado.'}
        </span>
      </div>
    </div>
  )
}

export default function ResumoDaConferencia({
  conferencia,
  carregando = false,
  erro = '',
  linkRecarga,
  onSoOsQueCabem,
  informativo = false,
  para,
  mensagem,
  rotuloDaAcao = 'Disparar',
  podeAgir = false,
  onAgir,
  dicaDaAcao,
  limitadoA = null,
  onDesfazerLimite,
  onConferirDeNovo,
  notaDoFiltro = false
}) {
  const theme = useTheme()
  const c = informativo ? paraOAgendamento(conferencia) : conferencia
  const avisos = (c?.motivos || []).filter((m) => m.tipo === 'aviso')
  const insuficiente = c?.veredito === 'saldo_insuficiente'
  const link = linkDeRecargaSeguro(linkRecarga)
  const validos = Number(c?.destinatarios?.validos) || 0
  const cabem = Number(c?.saldo?.cabem) || 0
  const liberado = c?.veredito === 'liberado' && !carregando && !erro

  return (
    <aside
      className={`dp dp-recibo${informativo ? ' informativo' : ''}`}
      style={tokensDoDisparo(theme)}
      aria-busy={carregando}
    >
      <h3>{informativo ? 'Custo estimado por ocorrência' : 'Resumo do disparo'}</h3>

      {(para !== undefined || !informativo) && (
        <div className="dp-lin">
          <span>Para</span>
          {para ? <b>{para}</b> : <b className="vazio">a escolher</b>}
        </div>
      )}
      {!informativo && (
        <div className="dp-lin">
          <span>Mensagem</span>
          {mensagem ? <b>{mensagem}</b> : <b className="vazio">a escolher</b>}
        </div>
      )}

      {limitadoA ? (
        <div className="dp-limitado">
          <span>Só os {numero(limitadoA)} primeiros da lista</span>
          <button type="button" className="dp-btn fantasma" onClick={onDesfazerLimite}>Desfazer</button>
        </div>
      ) : null}

      <Custo conferencia={c} carregando={carregando} informativo={informativo} />

      {c?.saldo && c?.custo?.cobravel && (
        <MedidorDeSaldo
          atual={c.saldo.atual}
          comprometido={c.saldo.comprometido}
          total={c.custo.total}
          tolerancia={c.saldo.tolerancia}
          rotuloDoEnvio={informativo ? 'Uma ocorrência' : 'Este disparo'}
        />
      )}

      <Selo conferencia={c} erro={erro} informativo={informativo} />

      {avisos.length > 0 && (
        <ul className="dp-avisos">
          {avisos.map((m) => <li key={m.codigo}>{m.texto}</li>)}
        </ul>
      )}

      {!informativo && insuficiente && (
        <div className="dp-escolhas">
          {link && (
            <a className="dp-escolha pri" href={link} target="_blank" rel="noopener noreferrer">
              <b>Recarregar saldo</b>
              <span>O disparo fica pronto aqui para você continuar.</span>
            </a>
          )}
          {cabem > 0 && onSoOsQueCabem && (
            <button type="button" className="dp-escolha" onClick={() => onSoOsQueCabem(cabem)}>
              <b>Disparar só para os {numero(cabem)} primeiros</b>
              <span>Os outros {numero(validos - cabem)} ficam fora deste disparo.</span>
            </button>
          )}
          {onConferirDeNovo && (
            <button type="button" className="dp-btn fantasma inicio" onClick={onConferirDeNovo}>
              Já recarreguei, conferir de novo
            </button>
          )}
        </div>
      )}

      {!informativo && !insuficiente && (
        <>
          <button
            type="button"
            className="dp-btn pri larga"
            disabled={!podeAgir || !liberado}
            onClick={() => { if (podeAgir && liberado && onAgir) onAgir() }}
          >
            {rotuloDaAcao}
          </button>
          {!podeAgir && dicaDaAcao && <span className="dp-rodape">{dicaDaAcao}</span>}
        </>
      )}

      {!informativo && notaDoFiltro && (
        <span className="dp-rodape">Regras do Filtro de números podem segurar parte dos envios.</span>
      )}
    </aside>
  )
}
